import React, { useCallback, useMemo, useState } from 'react';
import { DateRangeSelector } from '@components/DateRangeSelector';
import { useSubjectLanguageVariants } from '@features/user';
import { Box, FormControl, InputLabel, MenuItem, Select, Skeleton } from '@mui/material';

import type { RemittanceV2Item } from '../../../store';
import { useBulkEraDownload, useRemittancesV2Query } from '../../../store';
import { formatDateOnly } from '../../../utils';
import {
  type ColumnDef,
  DataTable,
  formatCents,
  Pagination,
  PayerFilter,
  SegmentControl,
  type SelectionAction,
  SelectionToolbar,
  TableToolbar,
  useBillingPayerOptionsQuery,
  useColumnPreferences,
  useTabFilters,
} from '../shared';

import { RemittanceDetailPane } from './RemittanceDetailPane';

const getDefaultColumns = (singularCap: string): ColumnDef[] => [
  { key: 'payment_date', label: 'Date', width: '90px', required: true },
  { key: 'payer_name', label: 'Payer', width: '1.2fr', required: true },
  { key: 'trace_number', label: 'Trace', width: '120px', type: 'mono' },
  { key: 'payment_method', label: 'Method', width: '80px' },
  { key: 'total_paid', label: 'Paid', width: '100px', type: 'mono', align: 'right', required: true },
  { key: 'claim_count', label: 'Claims', width: '70px', align: 'right' },
  { key: 'outcome', label: 'Outcome', width: '100px', type: 'chip', required: true },
  { key: 'patient_resp', label: `${singularCap} Resp.`, width: '100px', type: 'mono', align: 'right' },
];

function formatDate(iso: string | null | undefined): string {
  return formatDateOnly(iso, { month: 'short', day: 'numeric' }, '—');
}

export const RemittancesTab: React.FC = () => {
  const { singularCap } = useSubjectLanguageVariants();
  const DEFAULT_COLUMNS = useMemo(() => getDefaultColumns(singularCap), [singularCap]);

  const { data: payerStatsData, isLoading: payersLoading } = useBillingPayerOptionsQuery();
  const {
    activeSegment,
    setActiveSegment,
    filterValues,
    setFilterValue,
    page,
    setPage,
    perPage,
    setPerPage,
    dateRange,
    dateFrom,
    dateTo,
    onDateRangeChange,
    filtersOpen,
    toggleFilters,
  } = useTabFilters({ tabKey: 'remittances', defaultSegment: 'all', defaultPerPage: 50 });
  const [selectedRemittanceId, setSelectedRemittanceId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { allColumns, visibleKeys, visibleColumns, updatePreferences } =
    useColumnPreferences('remittances', DEFAULT_COLUMNS);

  const { downloadBulkEra, isDownloading: isBulkDownloading } = useBulkEraDownload();

  // Resolve payer name from UUID for the API (remittances uses text match)
  const selectedPayer = payerStatsData?.payers.find(p => p.payer_uuid === filterValues.payerUuid);

  const queryParams = useMemo(
    () => ({
      outcome: activeSegment !== 'all' ? activeSegment : undefined,
      payerName: selectedPayer?.payer_name || undefined,
      paymentMethod: filterValues.payment_method || undefined,
      dateFrom,
      dateTo,
      sortBy: 'payment_date' as const,
      sortOrder: 'desc' as const,
      page,
      perPage,
    }),
    [activeSegment, selectedPayer, filterValues, dateFrom, dateTo, page, perPage],
  );

  const { data, isLoading, isFetching } = useRemittancesV2Query(queryParams);

  const segments = useMemo(() => {
    return data?.segments ?? [];
  }, [data]);

  const rows = useMemo(() => {
    if (!data?.remittances) return [];
    return data.remittances.map((rem: RemittanceV2Item) => ({
      id: rem.remittance_id,
      remittance_id: rem.remittance_id,
      payment_date: formatDate(rem.payment_date),
      payer_name: rem.payer_name ?? '—',
      trace_number: rem.trace_number,
      payment_method: rem.payment_method ?? '—',
      total_paid: formatCents(rem.total_paid_cents),
      claim_count: rem.claim_count ?? 0,
      outcome: rem.outcome,
      outcome_label: rem.outcome_label,
      patient_resp: formatCents(rem.patient_responsibility_cents),
      _source_system: rem.source_system,
    }));
  }, [data]);

  const bulkActions = useMemo((): SelectionAction[] => {
    const actions: SelectionAction[] = [];
    // Only Stedi-sourced remittances can be downloaded
    const downloadableIds = rows
      .filter(r => selectedIds.has(r.remittance_id) && r._source_system === 'stedi')
      .map(r => r.remittance_id);

    if (downloadableIds.length > 0) {
      actions.push({
        label: `Download ERA (${downloadableIds.length})`,
        onClick: async () => {
          await downloadBulkEra(downloadableIds);
        },
        loading: isBulkDownloading,
      });
    }
    return actions;
  }, [rows, selectedIds, downloadBulkEra, isBulkDownloading]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilterValue(key, value);
  }, [setFilterValue]);

  const handleSegmentChange = useCallback((key: string) => {
    setActiveSegment(key);
    setSelectedIds(new Set());
  }, [setActiveSegment]);

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded"  sx={{ height: 42, width: 400 }}/>
        <Skeleton variant="rounded"  sx={{ height: 400 }}/>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SegmentControl segments={segments} active={activeSegment} onChange={handleSegmentChange} />
      <TableToolbar
        summary={data?.summary ?? ''}
        allColumns={allColumns}
        visibleKeys={visibleKeys}
        onColumnsChange={updatePreferences}
        filtersOpen={filtersOpen}
        onToggleFilters={toggleFilters}
        paginationData={data}
        onPageChange={setPage}
        filterExtra={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Box sx={{ width: 200 }}>
              <PayerFilter
                payers={payerStatsData?.payers ?? []}
                isLoading={payersLoading}
                value={filterValues.payerUuid ?? ''}
                onChange={(uuid) => handleFilterChange('payerUuid', uuid)}
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Method</InputLabel>
                <Select
                  value={filterValues.payment_method ?? ''}
                  onChange={e => handleFilterChange('payment_method', e.target.value)}
                  label="Method"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACH">ACH</MenuItem>
                  <MenuItem value="Check">Check</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: 200 }}>
              <DateRangeSelector
                value={dateRange}
                onChange={onDateRangeChange}
                label="Date Range"
                size="small"
              />
            </Box>
          </Box>
        }
      />
      <DataTable
        columns={visibleColumns}
        rows={rows}
        idKey="remittance_id"
        isFetching={isFetching}
        onRowClick={row => setSelectedRemittanceId(row.remittance_id as string)}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />
      {data && (
        <Pagination
          data={data}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
      <SelectionToolbar
        count={selectedIds.size}
        actions={bulkActions}
        onClear={() => setSelectedIds(new Set())}
      />
      <RemittanceDetailPane
        remittanceId={selectedRemittanceId}
        open={selectedRemittanceId !== null}
        onClose={() => setSelectedRemittanceId(null)}
      />
    </Box>
  );
};
